import type { JSX, RefObject } from 'preact';
import { useRef } from 'preact/hooks';
import { t } from '../i18n.js';
import {
  busy,
  destinationOf,
  form,
  groups,
  me,
  members,
  view,
  type FormKind,
} from '../state.js';
import { onUpload } from '../actions.js';
import { Popover } from './popover.js';
import {
  IconCamera,
  IconCollection,
  IconDoc,
  IconInvite,
  IconMedia,
  IconMembers,
  IconNewFolder,
  IconNote,
  IconPlus,
} from './icons.js';

/**
 * **Un seul « + », et il ajoute n'importe quoi.**
 *
 * Le constat d'usage était double, et les deux moitiés disaient la même chose :
 * sur l'app, la note avait son propre bouton flottant à côté du « + » ; sur le
 * site, elle avait un bouton posé à côté de l'envoi de fichiers ; et dans « Mes
 * fichiers », on ne pouvait pas créer de note du tout. Trois façons d'ajouter,
 * dont une qui manquait — c'est-à-dire que **le type décidait du geste**, ce que
 * l'incrément précédent avait justement entrepris de supprimer partout ailleurs.
 *
 * La règle est donc la même que celle du menu d'objet, prise à l'endroit : il y a
 * **un** point d'ajout par écran, il propose tout ce que l'écran peut recevoir, et
 * ce qu'il propose ne dépend pas du type mais du **lieu** — « Nouveau dossier »
 * n'existe que là où il y a une arborescence, parce qu'un dossier ne se range pas
 * dans une grille.
 *
 * Ce qui n'y figure pas dit encore la règle : pas de « Nouvelle collection » dans
 * *Mon espace*. Une collection naît attachée à un groupe, et la proposer depuis un
 * écran qui n'en montre aucun demanderait d'en choisir un d'abord.
 *
 * **Depuis 4.6, *Mes partages* rentre dans la règle.** La liste des groupes offre
 * « Nouveau groupe » ; un groupe ouvert offre « Nouvelle collection » et, si l'on
 * en est propriétaire, « Inviter ». Chacun de ces trois gestes vivait auparavant
 * dans son propre formulaire posé sur l'écran — c'est-à-dire trois façons
 * d'ajouter, exactement ce que l'incrément précédent avait supprimé ailleurs.
 *
 * ### Trois corrections du 2026-08-14, et deux venaient du téléphone
 *
 * ① **Le bouton est flottant sur un écran étroit**, comme dans l'app. Il vivait
 *    dans la barre de commandes, c'est-à-dire en haut, hors du pouce, et surtout
 *    à un endroit qui bouge avec le reste de la barre. L'app a un bouton flottant
 *    depuis toujours ; les deux clients répondaient différemment au même geste.
 * ② **Le menu est borné à l'écran.** Il était posé en `absolute` sur le bord
 *    droit de son bouton : à 390 px de large, une couche de 14 rem accrochée à un
 *    bouton déjà proche du bord gauche **sortait par la gauche**, et ses libellés
 *    devenaient illisibles. `Popover` le pose en `fixed` et le rentre dans les
 *    quatre bords — c'est la même correction que le menu d'objet avait reçue.
 * ③ **L'envoi se décline en trois entrées**, comme la feuille d'ajout de l'app :
 *    l'appareil photo, la photothèque, les documents. Ce n'est pas une commodité :
 *    un `<input type="file">` **sans `accept`** fait proposer par Chrome/Android
 *    l'enregistreur audio, ce qui déclenche une demande de permission au
 *    microphone avant même d'avoir choisi un fichier. On refuse, on arrive quand
 *    même au sélecteur — mais on a demandé le micro à quelqu'un qui envoyait une
 *    photo, ce qui ne s'explique pas. Chaque entrée porte donc l'`accept` de ce
 *    qu'elle promet, et aucune ne recouvre le son.
 */

/**
 * Ce qu'un document peut être. La liste est **explicite et non un joker**, et
 * c'est tout l'objet : `accept` absent rouvrirait l'enregistreur audio. Elle
 * couvre ce qu'un particulier range — bureautique, PDF, texte, archives — et les
 * types MIME généraux y côtoient les extensions, parce qu'Android résout les uns
 * et Windows les autres.
 */
const DOC_ACCEPT = [
  'application/pdf',
  'text/*',
  'application/zip',
  'application/epub+zip',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  '.pdf,.txt,.md,.csv,.rtf,.zip,.epub,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp',
].join(',');

const MEDIA_ACCEPT = 'image/*,video/*';

export function AddMenu(): JSX.Element | null {
  const v = view.value;
  const destination = destinationOf(v);
  const camera = useRef<HTMLInputElement>(null);
  const media = useRef<HTMLInputElement>(null);
  const files = useRef<HTMLInputElement>(null);

  // Une collection **se regarde** comme une grille, mais elle ne reçoit rien : y
  // verser un objet se fait depuis l'objet (« Ajouter à une collection… »), et
  // proposer ici un envoi qui atterrirait dans un dossier serait un geste dont le
  // résultat n'apparaît pas sur l'écran d'où on l'a lancé.
  //
  // **L'administration non plus**, et l'oubli se voyait : le seul bouton coloré
  // de la console proposait « Nouvelle note » et « Envoyer des fichiers » sur un
  // écran dont l'action est de créer un compte. Un bouton primaire qui fait autre
  // chose que ce que la page fait est pire qu'un bouton absent.
  if (v.kind === 'collection' || v.kind === 'me' || v.kind === 'admin') return null;

  const choose = (what: FormKind): void => void (form.value = what);

  return (
    <div class="add">
      {v.kind === 'shared' || v.kind === 'group' ? (
        <GroupAdd view={v} choose={choose} />
      ) : (
        <AddTrigger>
          {(close) => (
            <>
              <button
                type="button"
                role="menuitem"
                data-testid="new-note"
                onClick={() => {
                  close();
                  choose('note');
                }}
              >
                <IconNote size={18} />
                <span>{t('space.newNote')}</span>
              </button>
              {/* **Le dossier n'est proposé que là où il se voit.** Le créer depuis
                  la grille produirait un dossier qu'aucun écran de cette
                  destination ne montre — un geste sans retour visible se lit
                  comme une panne. */}
              {destination === 'space' ? (
                <button
                  type="button"
                  role="menuitem"
                  data-testid="new-folder"
                  onClick={() => {
                    close();
                    choose('folder');
                  }}
                >
                  <IconNewFolder size={18} />
                  <span>{t('space.newFolder')}</span>
                </button>
              ) : null}

              <hr />

              {/* L'ordre est celui de la feuille de l'app : ce qu'on **fait**
                  d'abord, ce qu'on **a** ensuite, ce qui est **rangé** en
                  dernier. Les trois ouvrent le même protocole d'envoi ; seule
                  change la porte que le système leur ouvre. */}
              <button
                type="button"
                role="menuitem"
                data-testid="add-camera"
                onClick={() => {
                  close();
                  camera.current?.click();
                }}
              >
                <IconCamera size={18} />
                <span>{t('add.camera')}</span>
              </button>
              <button
                type="button"
                role="menuitem"
                data-testid="add-media"
                onClick={() => {
                  close();
                  media.current?.click();
                }}
              >
                <IconMedia size={18} />
                <span>{t('add.media')}</span>
              </button>
              <button
                type="button"
                role="menuitem"
                data-testid="add-upload"
                onClick={() => {
                  close();
                  files.current?.click();
                }}
              >
                <IconDoc size={18} />
                <span>{t('add.documents')}</span>
              </button>
            </>
          )}
        </AddTrigger>
      )}

      {/* **Les entrées de fichiers restent montées en permanence**, cachées. Un
          `<input type="file">` est le seul élément qu'un navigateur laisse ouvrir
          un sélecteur, et il ne peut être déclenché que s'il existe : le monter
          avec le menu le ferait disparaître avant que le choix ne revienne.
          Elles sont **hors** de la couche, qui n'est montée qu'ouverte. */}
      <FileInput testId="camera-input" input={camera} accept="image/*" capture />
      <FileInput testId="media-input" input={media} accept={MEDIA_ACCEPT} />
      <FileInput testId="upload-input" input={files} accept={DOC_ACCEPT} />
    </div>
  );
}

/**
 * Le bouton et sa couche. **`Popover` et non un bloc absolu** : la couche est
 * `fixed` et rentrée dans les quatre bords de l'écran, ce qui est la seule forme
 * qui tienne à 390 px de large — et la même que celle du menu d'objet.
 */
function AddTrigger({
  children,
}: {
  readonly children: (close: () => void) => JSX.Element;
}): JSX.Element {
  return (
    <Popover
      testId="add"
      className="add-host"
      buttonClass="btn primary add-fab"
      label={t('add.label')}
      title={t('add.label')}
      width={260}
      align="end"
      trigger={() => (
        <>
          <IconPlus size={20} />
          {/* Le libellé disparaît quand le bouton devient flottant : un bouton
              rond de 56 px est celui que tout le monde reconnaît, et le mot y
              tiendrait mal. Il reste le nom accessible, qui lui ne dépend pas de
              la largeur de l'écran. */}
          <span class="add-label">{t('add.label')}</span>
        </>
      )}
    >
      {children}
    </Popover>
  );
}

/**
 * Une entrée de fichiers. **L'`accept` n'est pas une commodité** : sans lui,
 * Chrome/Android propose l'enregistreur audio et demande le microphone avant même
 * qu'on ait choisi quoi que ce soit.
 */
function FileInput({
  testId,
  input,
  accept,
  capture,
}: {
  readonly testId: string;
  readonly input: RefObject<HTMLInputElement>;
  readonly accept: string;
  /** Ouvre l'appareil photo plutôt que la photothèque, là où le système le sait. */
  readonly capture?: boolean;
}): JSX.Element {
  return (
    <input
      type="file"
      // Un cliché est unique par nature ; le reste s'envoie par lots.
      multiple={!capture}
      accept={accept}
      {...(capture ? { capture: 'environment' } : {})}
      class="visually-hidden"
      data-testid={testId}
      aria-hidden="true"
      tabIndex={-1}
      ref={input}
      disabled={Boolean(busy.value)}
      onChange={(ev) => {
        const node = ev.currentTarget as HTMLInputElement;
        const chosen = [...(node.files ?? [])];
        // Remis à zéro : sans cela, renvoyer *le même* fichier n'émet pas de
        // `change`, et le second envoi ne partirait jamais.
        node.value = '';
        if (chosen.length > 0) void onUpload(chosen);
      }}
    />
  );
}

/**
 * Le « + » des partages. Même bouton, même place — **ce qu'il propose dépend du
 * lieu** : la liste des groupes ne peut recevoir qu'un groupe, un groupe ouvert
 * une collection, et une invitation n'est offerte qu'à qui peut l'émettre.
 *
 * Proposer « Inviter » à un membre simple produirait un `403` après le clic : la
 * règle est celle de l'historique des notes — on n'affiche pas un bouton grisé,
 * on n'affiche **aucun** bouton.
 */
function GroupAdd({
  view: v,
  choose,
}: {
  readonly view: { readonly kind: 'shared' } | { readonly kind: 'group'; readonly gid: string };
  readonly choose: (what: FormKind) => void;
}): JSX.Element {
  const roster = v.kind === 'group' ? members.value[v.gid] : undefined;
  const owner =
    v.kind === 'group' &&
    (roster?.some((m) => m.uid === me.value?.uid && m.role === 'owner') ??
      (groups.value ?? []).find((g) => g.gid === v.gid)?.role === 'owner');

  return (
    <AddTrigger>
      {(close) =>
        v.kind === 'shared' ? (
          <button
            type="button"
            role="menuitem"
            data-testid="new-group"
            onClick={() => {
              close();
              choose('group');
            }}
          >
            <IconMembers size={18} />
            <span>{t('group.new')}</span>
          </button>
        ) : (
          <>
            <button
              type="button"
              role="menuitem"
              data-testid="new-collection"
              onClick={() => {
                close();
                choose('collection');
              }}
            >
              <IconCollection size={18} />
              <span>{t('collection.newName')}</span>
            </button>
            {owner ? (
              <button
                type="button"
                role="menuitem"
                data-testid="new-invite"
                onClick={() => {
                  close();
                  choose('invite');
                }}
              >
                <IconInvite size={18} />
                <span>{t('group.invite')}</span>
              </button>
            ) : null}
          </>
        )
      }
    </AddTrigger>
  );
}
